import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Helmet } from 'react-helmet';
import Header from '../../components/ui/Header';
import Icon from '../../components/AppIcon';
import Button from '../../components/ui/Button';
import SkillCategory from './components/SkillCategory';
import ProficiencyRadar from './components/ProficiencyRadar';
import CodePlayground from './components/CodePlayground';
import CertificationBadges from './components/CertificationBadges';

const ArsenalShowcase = () => {
  const [activeCategory, setActiveCategory] = useState('Frontend Mastery');
  const [currentSection, setCurrentSection] = useState('overview');
  const [isLoading, setIsLoading] = useState(true);

  const categories = [
    'Frontend Mastery',
    'Backend Architecture', 
    'Design Systems',
    'Emerging Technologies'
  ];

  const sections = [
    { id: 'overview', label: 'Arsenal Overview', icon: 'Zap' },
    { id: 'proficiency', label: 'Proficiency Matrix', icon: 'Target' },
    { id: 'playground', label: 'Code Playground', icon: 'Terminal' },
    { id: 'certifications', label: 'Certifications', icon: 'Award' }
  ];

  useEffect(() => {
    const timer = setTimeout(() => {
      setIsLoading(false);
    }, 1000);
    return () => clearTimeout(timer);
  }, []);

  const scrollToSection = (sectionId) => {
    setCurrentSection(sectionId);
    const element = document.getElementById(sectionId);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
    }
  };

  if (isLoading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="text-center">
          <div className="w-16 h-16 border-4 border-brand-cyan border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
          <div className="font-orbitron font-bold text-xl text-brand-cyan mb-2">
            Initializing Arsenal
          </div>
          <div className="text-text-secondary font-mono text-sm">
            Loading weapon systems...
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      <Helmet>
        <title>Arsenal Showcase - Skills & Technologies | CyberDev Nexus</title>
        <meta name="description" content="Explore my comprehensive technology arsenal featuring interactive skill radars, proficiency visualizations, and cutting-edge development capabilities." />
        <meta name="keywords" content="skills, technologies, React, Node.js, AWS, frontend, backend, full-stack developer" />
      </Helmet>

      <Header />

      {/* Hero Section */}
      <section className="relative pt-24 pb-16 overflow-hidden">
        <div className="absolute inset-0 particle-bg"></div>
        
        <div className="relative max-w-7xl mx-auto px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            className="text-center mb-16"
          >
            <div className="inline-flex items-center space-x-4 mb-6">
              <div className="w-16 h-16 bg-gradient-to-br from-brand-cyan to-brand-purple rounded-2xl flex items-center justify-center cyber-glow-intense">
                <Icon name="Zap" size={32} color="#ffffff" />
              </div>
              <div className="text-left">
                <h1 className="font-orbitron font-black text-4xl lg:text-6xl text-foreground">
                  Arsenal Showcase
                </h1>
                <p className="font-exo text-xl text-brand-cyan">
                  Skills & Technologies Matrix
                </p>
              </div>
            </div>
            
            <p className="text-xl text-text-secondary max-w-3xl mx-auto mb-8">
              Welcome to my digital armory - an immersive exploration of cutting-edge technologies, 
              battle-tested skills, and experimental protocols that power next-generation web experiences.
            </p>

            {/* Quick Stats */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-6 max-w-4xl mx-auto">
              {[
                { label: 'Technologies', value: '25+', icon: 'Code', color: '#00d4ff' },
                { label: 'Years Experience', value: '5+', icon: 'Clock', color: '#00ff88' },
                { label: 'Projects Built', value: '100+', icon: 'Layers', color: '#8844ff' },
                { label: 'Certifications', value: '8', icon: 'Award', color: '#ff6b35' }
              ].map((stat, index) => (
                <motion.div
                  key={stat.label}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.2 + index * 0.1 }}
                  className="glass-morphism p-4 rounded-lg cyber-border"
                >
                  <div 
                    className="w-10 h-10 rounded-lg flex items-center justify-center mx-auto mb-2"
                    style={{ background: `linear-gradient(135deg, ${stat.color}80, ${stat.color}40)` }}
                  >
                    <Icon name={stat.icon} size={20} color="#ffffff" />
                  </div>
                  <div className="text-2xl font-orbitron font-bold text-foreground">
                    {stat.value}
                  </div>
                  <div className="text-sm text-text-secondary">
                    {stat.label}
                  </div>
                </motion.div>
              ))}
            </div>
          </motion.div>

          {/* Navigation Menu */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.6 }}
            className="flex justify-center mb-16"
          >
            <div className="glass-morphism p-2 rounded-2xl cyber-border">
              <div className="flex flex-wrap justify-center gap-2">
                {sections.map((section) => (
                  <Button
                    key={section.id}
                    variant={currentSection === section.id ? "default" : "ghost"}
                    size="sm"
                    iconName={section.icon}
                    iconPosition="left"
                    iconSize={16}
                    onClick={() => scrollToSection(section.id)}
                    className={`${currentSection === section.id ? 'cyber-glow' : ''} transition-all duration-300`}
                  >
                    {section.label}
                  </Button>
                ))}
              </div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Arsenal Overview */}
      <section id="overview" className="py-16">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-12"
          >
            <h2 className="font-orbitron font-bold text-3xl text-foreground mb-4">
              Technology Arsenal
            </h2>
            <p className="text-text-secondary max-w-2xl mx-auto">
              Interactive skill categories showcasing proficiency levels, project experience, and technological mastery across multiple domains.
            </p>
          </motion.div>

          <div className="grid lg:grid-cols-2 gap-8">
            {categories.map((category, index) => (
              <motion.div
                key={category}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
              >
                <SkillCategory
                  category={category}
                  isActive={activeCategory === category}
                  onActivate={() => setActiveCategory(category)}
                />
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Proficiency Matrix */}
      <section id="proficiency" className="py-16 bg-surface/20">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <ProficiencyRadar />
          </motion.div>
        </div>
      </section>

      {/* Code Playground */}
      <section id="playground" className="py-16">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <CodePlayground />
          </motion.div>
        </div>
      </section>

      {/* Removed Future Protocols and Technology Timeline per portfolio focus */}

      {/* Certifications */}
      <section id="certifications" className="py-16 bg-surface/20">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <CertificationBadges />
          </motion.div>
        </div>
      </section>

      {/* Call to Action */}
      <section className="py-16">
        <div className="max-w-4xl mx-auto px-6 lg:px-8 text-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="glass-morphism p-12 rounded-2xl cyber-border"
          >
            <div className="w-16 h-16 bg-gradient-to-br from-brand-cyan to-brand-purple rounded-2xl flex items-center justify-center mx-auto mb-6 cyber-glow-intense">
              <Icon name="Rocket" size={32} color="#ffffff" />
            </div>
            
            <h2 className="font-orbitron font-bold text-3xl text-foreground mb-4">
              Ready to Build the Future?
            </h2>
            <p className="text-text-secondary mb-8 max-w-2xl mx-auto">
              Let's collaborate on your next project and bring cutting-edge technology solutions to life. 
              From concept to deployment, I'll help you navigate the digital frontier.
            </p>
            
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link to="/neural-network-contact-collaboration">
                <Button
                  variant="default"
                  size="lg"
                  iconName="MessageSquare"
                  iconPosition="left"
                  iconSize={20}
                  className="cyber-glow-intense"
                >
                  Start a Project
                </Button>
              </Link>
              <Link to="/project-dimensions-portfolio">
                <Button
                  variant="outline"
                  size="lg"
                  iconName="Eye"
                  iconPosition="left"
                  iconSize={20}
                  className="cyber-border hover:cyber-glow"
                >
                  View Portfolio
                </Button>
              </Link>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Footer */}
      <footer className="py-8 border-t border-border">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="flex flex-col md:flex-row items-center justify-between">
            <div className="flex items-center space-x-3 mb-4 md:mb-0">
              <div className="w-8 h-8 bg-gradient-to-br from-brand-cyan to-brand-purple rounded-lg flex items-center justify-center">
                <Icon name="Cpu" size={16} color="#ffffff" />
              </div>
              <span className="font-orbitron font-bold text-foreground">CyberDev Nexus</span>
            </div>
            <div className="text-sm text-text-secondary">
              © {new Date().getFullYear()} Arsenal Showcase. All systems operational.
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default ArsenalShowcase;